import { IRoom, Logger, loggerManager } from '..';
import ErrorCode from '../common/error-code';
import { generateErrorMessage } from '../common/error-generator';
import { ErrorName } from '../common/error-info';
import RtcError from '../common/rtc-error';
import kvStatManager, { KV_REPORT_KEY_PLAYBACK_QUALITY_SWITCHER } from '../manager/kv-stat-manager';
import { TRTCEvent } from '../../../api-cloud-next/src/trtc-event';
import { TRTCStreamType } from '../../../api-cloud-next/src/type';
import type TRTCClass from '../../../api-cloud-next/src/main';
import type { TRTCStatistics } from '../../../api-cloud-next/src/type';

export interface PlaybackQualityStream {
  name: string;
  userId: string;
  streamType?: TRTCStreamType;
  /** 码率 (kbps)，用于排序，从高到低 */
  bitrate: number;
}

export interface ResolutionConfig {
  userId: string;
  streamType: TRTCStreamType;
  name: string;
}

export interface SwitchPlaybackQualityOptions {
  quality?: string;
  streamList?: PlaybackQualityStream[];
  /**
   * 切换分辨率成功的回调
   * @param from 切换前的分辨率配置
   * @param to 切换后的分辨率配置
   */
  onSwitched?: (from: ResolutionConfig, to: ResolutionConfig) => void;
}

/** auto 模式的网络质量阈值配置 */
interface AutoSwitchConfig {
  /** 丢包率差阈值 (%) */
  lossPoorLimit: number;
  /** 丢包率好阈值 (%)，用于上切判断 */
  lossGoodLimit: number;
  /** 解码帧率差阈值 (fps) */
  fpsPoorLimit: number;
  /** 渲染卡顿时长阈值 (ms) */
  renderFreezeLimit: number;
  /** 下切后冷却时间 (ms)，上切需要距离上次下切超过此时间 */
  cooldownTime: number;
  /** 上切连续失败次数上限，达到后不再尝试上切 */
  maxUpgradeFailCount: number;
}

type AutoSwitchReason = 'init' | 'upgrade' | 'downgrade' | 'decode-fallback';

type PlaybackQualityErrorLike = {
  extraCode?: number;
  data?: {
    userId?: string;
    streamType?: TRTCStreamType;
  };
  userId?: string;
  streamType?: TRTCStreamType;
};

const VIDEO_DECODE_FAILED_EXTRA_CODE = 5507;

const DEFAULT_AUTO_SWITCH_CONFIG: AutoSwitchConfig = {
  lossPoorLimit: 20,
  lossGoodLimit: 15,
  fpsPoorLimit: 5,
  renderFreezeLimit: 1500,
  cooldownTime: 120 * 1000,
  maxUpgradeFailCount: 3
};

export class PlaybackQualitySwitcher {
  private log: Logger;
  private switchTimeoutMs = 10 * 1000;

  /** auto 模式核心状态 */
  private autoMode = {
    enabled: false,
    instance: null as TRTCClass | null,
    config: DEFAULT_AUTO_SWITCH_CONFIG,
    sortedStreamList: [] as PlaybackQualityStream[],
    currentQualityIndex: 0, // 当前播放的质量索引（0=最高质量）
    bestAllowedQualityIndex: 0, // 当前会话内自动模式可尝试的最高画质索引
    lastSwitchReason: 'init' as AutoSwitchReason,
    lastSwitchTargetIndex: 0,
    onSwitched: undefined as ((from: ResolutionConfig, to: ResolutionConfig) => void) | undefined // 保存用户传入的切换回调
  };

  /** 切换控制状态 */
  private switchControl = {
    isInternal: false, // 标记是否为内部自动切换
    isSwitching: false, // 标记是否正在切换中，防止重复切换
    lastDowngradeTime: 0, // 上次下切时间，用于上切冷却判断
    boundOnStatistics: null as ((stats: TRTCStatistics) => void) | null, // 保存绑定的监听函数引用
    boundOnError: null as ((error: PlaybackQualityErrorLike) => void) | null // 保存绑定的 ERROR 事件监听函数引用
  };

  /** 当前网络指标 */
  private networkMetrics = {
    frameRate: 0, // 当前解码帧率
    loss: 0, // 当前丢包率
    renderFreezeDuration: 0 // 当前周期渲染卡顿时长 (ms)
  };

  /** 质量切换计数器 */
  private counters = {
    lossUnder: 0, // 丢包率达标连续次数
    upgradeFail: 0 // 上切连续失败次数
  };

  /** fps 改善率采样状态 */
  private fpsImprovement = {
    triggerFps: 0, // 切换触发时的 fps
    afterSwitchFpsQueue: [] as number[], // 切换后采集的 fps 样本
    sampling: false, // 是否正在采样中
    samplingStartTime: 0 // 采样开始时间
  };

  private static readonly FPS_SAMPLING_DURATION = 6000; // fps 采样持续 6s

  constructor() {
    this.log = loggerManager.createLogger({
      id: 'pqs'
    });
  }

  /**
   * 获取当前正在播放的直播流信息
   * 遍历 streamList 查找 isPlayCalled 的流
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getCurrentPlayingStream(trtcInstance: TRTCClass): { userId: string; streamType: TRTCStreamType; config: any } | null {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const instance = trtcInstance as any;
    const streamList = instance._playbackQualityList;
    if (!streamList || streamList.length === 0) {
      this.log.warn('getCurrentPlayingStream: streamList is empty');
      return null;
    }
    for (const stream of streamList) {
      const user = trtcInstance.room.remotePublishedUserMap.get(stream.userId);
      if (!user) continue;

      const streamType = stream.streamType ?? TRTCStreamType.Main;
      const track = streamType === TRTCStreamType.Sub
        ? user.remoteAuxiliaryTrack
        : user.remoteVideoTrack;

      if (track.isPlayCalled) {
        const config = instance._remoteVideoConfigMap.get(`${stream.userId}_${streamType}`)?.config;
        if (config) return { userId: stream.userId, streamType, config };
      }
    }
    return null;
  }

  async switchPlaybackQuality(options: { trtcInstance: TRTCClass } & SwitchPlaybackQualityOptions & { _internalSwitch?: boolean }) {
    const { trtcInstance, streamList, quality, onSwitched, _internalSwitch = false } = options;
    this.log.info(`switchPlaybackQuality quality: ${quality}, streamList: ${streamList ? JSON.stringify(streamList) : '(cached)'}, _internalSwitch: ${_internalSwitch}`);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const instance = trtcInstance as any;
    // 缓存 streamList
    if (streamList && streamList.length > 0) {
      instance._playbackQualityList = streamList.map(stream => ({
        ...stream,
        streamType: (stream.streamType ?? TRTCStreamType.Main) as TRTCStreamType
      }));
    }
    // auto 模式
    if (quality === 'auto') {
      await this.startAutoMode(trtcInstance, onSwitched);
      return;
    }
    // 关闭 auto 模式（外部手动切换到具体质量时）
    if (this.autoMode.enabled && quality && !this.switchControl.isInternal) {
      this.stopAutoMode();
    }
    // 仅设置 streamList 时不切换
    if (!quality) return;
    if (!instance._playbackQualityList || instance._playbackQualityList.length <= 0) {
      this.log.warn('switchPlaybackQuality: streamList is empty, please call with streamList first');
      return;
    }
    // 校验 quality 是否在 streamList 中
    const targetStream = instance._playbackQualityList.find((stream: { name: string }) => stream.name === quality);
    if (!targetStream) {
      this.log.warn(`switchPlaybackQuality: quality "${quality}" not found in streamList`);
      return;
    }
    // 找到正在播放的流
    const currentPlaying = this.getCurrentPlayingStream(trtcInstance);
    this.log.info(`currentPlaying userId: ${currentPlaying?.userId}, streamType: ${currentPlaying?.streamType}`);
    if (!currentPlaying) return;
    // 先 start 新流，等待首帧后再 stop 旧流，实现无闪烁切换
    const newStreamType = targetStream.streamType ?? TRTCStreamType.Main;
    // 如果目标流和当前流相同，跳过切换
    if (currentPlaying.userId === targetStream.userId && currentPlaying.streamType === newStreamType) {
      this.log.info('switchPlaybackQuality: already playing target stream');
      return;
    }
    if (this.switchControl.isSwitching && !_internalSwitch) {
      this.log.info('switchPlaybackQuality: switching in progress, skip');
      return;
    }
    if (!_internalSwitch) {
      this.switchControl.isSwitching = true;
    }
    const oldConfig = { ...currentPlaying.config };

    const bothMain = oldConfig.streamType === TRTCStreamType.Main && newStreamType === TRTCStreamType.Main;

    // 手动切换时判断上切/下切（通过 bitrate 比较）
    let manualResultKey: number | undefined;
    if (!_internalSwitch) {
      const currentStream = instance._playbackQualityList.find(
        (s: PlaybackQualityStream) => s.userId === currentPlaying.userId && (s.streamType ?? TRTCStreamType.Main) === currentPlaying.streamType
      );
      const isUpgrade = currentStream && targetStream.bitrate > currentStream.bitrate;
      manualResultKey = isUpgrade ? KV_REPORT_KEY_PLAYBACK_QUALITY_SWITCHER.MANUAL_UPGRADE_RESULT : KV_REPORT_KEY_PLAYBACK_QUALITY_SWITCHER.MANUAL_DOWNGRADE_RESULT;
    }

    // 超时 Promise（覆盖 startRemoteVideo 到首帧渲染的整个切换过程）
    let timeoutTimer: ReturnType<typeof setTimeout> | null = null;
    const timeoutPromise = new Promise<'timeout'>(resolve => {
      timeoutTimer = setTimeout(() => resolve('timeout'), this.switchTimeoutMs);
    });

    try {
      // 1. 拉新流视频
      const startPromise = trtcInstance.startRemoteVideo({
        ...currentPlaying.config,
        userId: targetStream.userId,
        streamType: newStreamType,
        option: {
          ...currentPlaying.config.option,
          isLiveStream: true
        }
      });

      // 2. 等待 PLAYING 状态或超时
      const result = await Promise.race([startPromise, timeoutPromise]);
      if (result === 'timeout') {
        // 超时：停止新流，保持旧流不变，抛出错误
        this.log.error('switchPlaybackQuality: target stream start timeout, rollback');
        await this.rollbackTargetStream(trtcInstance, targetStream.userId, newStreamType);
        throw new RtcError({
          code: ErrorCode.SUBSCRIPTION_TIMEOUT,
          message: generateErrorMessage({
            key: ErrorName.SWITCH_PLAYBACK_QUALITY_TIMEOUT,
            data: { userId: targetStream.userId }
          })
        });
      }
      // PLAYING 成功后，等待新流首帧真正渲染到屏幕，避免闪烁
      const firstFrameResult = await Promise.race([
        this.waitForFirstFrameRendered(trtcInstance, targetStream.userId, newStreamType).then(() => 'ready' as const),
        timeoutPromise
      ]);
      if (firstFrameResult === 'timeout') {
        this.log.error('switchPlaybackQuality: target stream first frame timeout, rollback');
        await this.rollbackTargetStream(trtcInstance, targetStream.userId, newStreamType);
        throw new RtcError({
          code: ErrorCode.SUBSCRIPTION_TIMEOUT,
          message: generateErrorMessage({
            key: ErrorName.SWITCH_PLAYBACK_QUALITY_TIMEOUT,
            data: { userId: targetStream.userId }
          })
        });
      }

      // 首帧渲染完成后：拉起新流音频 + 停旧流视频 + 停旧流音频
      const stopOldVideoPromise = trtcInstance.stopRemoteVideo(oldConfig);
      if (bothMain) {
        await Promise.all([
          trtcInstance.muteRemoteAudio(targetStream.userId, false).catch(e => {
            this.log.warn('muteRemoteAudio(new, false) failed', e);
          }),
          trtcInstance.muteRemoteAudio(oldConfig.userId, true).catch(e => {
            this.log.warn('muteRemoteAudio(old, true) failed', e);
          }),
          stopOldVideoPromise
        ]);
      } else {
        await stopOldVideoPromise;
      }

      // 切换成功后更新当前播放的直播流信息
      instance._currentLiveUserId = targetStream.userId;
      instance._currentLiveStreamType = newStreamType;

      // 构建切换前后的分辨率配置并触发回调
      const currentStream = instance._playbackQualityList.find(
        (s: PlaybackQualityStream) => s.userId === currentPlaying.userId && (s.streamType ?? TRTCStreamType.Main) === currentPlaying.streamType
      );
      const fromConfig: ResolutionConfig = {
        userId: currentPlaying.userId,
        streamType: currentPlaying.streamType,
        name: currentStream?.name || ''
      };
      const toConfig: ResolutionConfig = {
        userId: targetStream.userId,
        streamType: newStreamType,
        name: targetStream.name
      };
      if (onSwitched) {
        onSwitched(fromConfig, toConfig);
      }

      if (!_internalSwitch && manualResultKey) {
        kvStatManager.addSuccessEvent({ key: manualResultKey });
      }
    } catch (error) {
      if (!_internalSwitch && manualResultKey) {
        kvStatManager.addFailedEvent({ key: manualResultKey, error: error as RtcError });
      }
      throw error;
    } finally {
      if (timeoutTimer) {
        clearTimeout(timeoutTimer);
      }
      if (!_internalSwitch) {
        this.switchControl.isSwitching = false;
      }
    }
  }

  private waitForFirstFrameRendered(trtcInstance: TRTCClass, userId: string, streamType: TRTCStreamType): Promise<void> {
    const user = trtcInstance.room.remotePublishedUserMap.get(userId);
    if (!user) return Promise.resolve();

    const track = streamType === TRTCStreamType.Sub ? user.remoteAuxiliaryTrack : user.remoteVideoTrack;
    const videoElement = track?.player?.getElement?.() as HTMLVideoElement | null;
    if (!videoElement) return Promise.resolve();

    if (typeof videoElement.requestVideoFrameCallback !== 'function') {
      return Promise.resolve();
    }
    return new Promise<void>(resolve => {
      videoElement.requestVideoFrameCallback(() => resolve());
    });
  }

  private async rollbackTargetStream(trtcInstance: TRTCClass, userId: string, streamType: TRTCStreamType) {
    try {
      await trtcInstance.stopRemoteVideo({
        userId,
        streamType
      });
    } catch (rollbackError) {
      this.log.warn('switchPlaybackQuality: rollback stopRemoteVideo failed', rollbackError);
    }
  }

  private getQualityIndex(userId: string, streamType: TRTCStreamType) {
    return this.autoMode.sortedStreamList.findIndex(stream => stream.userId === userId && (stream.streamType ?? TRTCStreamType.Main) === streamType);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getDecodeFailedStream(error: PlaybackQualityErrorLike): { userId: string; streamType: TRTCStreamType } | null {
    const userId = error?.data?.userId ?? error?.userId;
    if (!userId) return null;

    return {
      userId,
      streamType: (error?.data?.streamType ?? error?.streamType ?? TRTCStreamType.Main) as TRTCStreamType
    };
  }

  /**
   * 启动 auto 模式
   */
  private async startAutoMode(trtcInstance: TRTCClass, onSwitched?: (from: ResolutionConfig, to: ResolutionConfig) => void) {
    if (this.autoMode.enabled) {
      this.log.info('auto mode already enabled');
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const instance = trtcInstance as any;
    if (!instance._playbackQualityList || instance._playbackQualityList.length <= 1) {
      this.log.warn('startAutoMode: need at least 2 streams in streamList for auto mode');
      return;
    }

    this.autoMode.enabled = true;
    this.autoMode.instance = trtcInstance;
    this.autoMode.onSwitched = onSwitched;
    this.counters.lossUnder = 0;
    this.counters.upgradeFail = 0;
    this.switchControl.lastDowngradeTime = 0;
    this.autoMode.bestAllowedQualityIndex = 0;
    this.autoMode.lastSwitchReason = 'init';
    this.autoMode.lastSwitchTargetIndex = 0;

    // 按 bitrate 从高到低排序
    const streamList: PlaybackQualityStream[] = instance._playbackQualityList || [];
    this.autoMode.sortedStreamList = [...streamList].sort((a, b) => b.bitrate - a.bitrate);
    this.log.info(`auto mode streams: ${this.autoMode.sortedStreamList.map(s => `${s.name}(${s.bitrate}kbps)`).join(' > ')}`);

    // 找到当前播放流的索引
    const currentPlaying = this.getCurrentPlayingStream(trtcInstance);
    if (currentPlaying) {
      const { userId, streamType } = currentPlaying;
      const idx = this.autoMode.sortedStreamList.findIndex(s => s.userId === userId && (s.streamType ?? TRTCStreamType.Main) === streamType);
      this.autoMode.currentQualityIndex = idx >= 0 ? idx : 0;
    } else {
      this.autoMode.currentQualityIndex = 0;
    }
    this.autoMode.lastSwitchTargetIndex = this.autoMode.currentQualityIndex;

    // 监听统计事件（获取网络指标和帧率，并触发质量检测）
    this.switchControl.boundOnStatistics = this.onStatistics;
    trtcInstance.on(TRTCEvent.STATISTICS, this.switchControl.boundOnStatistics);

    // 监听 ERROR 事件（检测解码失败，立即下切）
    this.switchControl.boundOnError = this.onError;
    trtcInstance.on(TRTCEvent.ERROR, this.switchControl.boundOnError);

    this.log.info('auto mode started');
  }

  /**
   * 停止 auto 模式
   */
  private stopAutoMode() {
    if (!this.autoMode.enabled) return;

    // 使用保存的监听函数引用移除事件监听，避免 instance 被意外置空导致泄漏
    if (this.autoMode.instance && this.switchControl.boundOnStatistics) {
      this.autoMode.instance.off(TRTCEvent.STATISTICS, this.switchControl.boundOnStatistics);
    }
    if (this.autoMode.instance && this.switchControl.boundOnError) {
      this.autoMode.instance.off(TRTCEvent.ERROR, this.switchControl.boundOnError);
    }
    this.switchControl.boundOnStatistics = null;
    this.switchControl.boundOnError = null;

    this.autoMode.enabled = false;
    this.autoMode.instance = null;
    this.autoMode.onSwitched = undefined;
    this.autoMode.sortedStreamList = [];
    this.autoMode.currentQualityIndex = 0;
    this.autoMode.bestAllowedQualityIndex = 0;
    this.autoMode.lastSwitchReason = 'init';
    this.autoMode.lastSwitchTargetIndex = 0;
    this.counters.lossUnder = 0;
    this.counters.upgradeFail = 0;
    this.networkMetrics.frameRate = 0;
    this.networkMetrics.loss = 0;
    this.networkMetrics.renderFreezeDuration = 0;
    this.switchControl.isSwitching = false;
    this.fpsImprovement.sampling = false;

    this.log.info('auto mode stopped');
  }

  /**
   * 统计数据回调 - 获取网络指标和帧率，并触发质量检测
   */
  private onStatistics = (stats: TRTCStatistics) => {
    if (!this.autoMode.instance) return;

    const { config } = this.autoMode;

    // 1. 更新网络指标（丢包率）
    this.networkMetrics.loss = stats.downLoss;

    // 2. 更新好网络计数（用于上切判断）
    this.counters.lossUnder = stats.downLoss <= config.lossGoodLimit ? this.counters.lossUnder + 1 : 0;

    // 3. 获取当前播放流的帧率
    if (!stats?.remoteStatistics?.length) return;

    const currentPlaying = this.getCurrentPlayingStream(this.autoMode.instance);
    if (!currentPlaying) return;

    const { userId, streamType } = currentPlaying;
    const remote = stats.remoteStatistics.find(r => r.userId === userId);
    if (!remote?.video?.length) return;

    const targetVideoType = streamType === TRTCStreamType.Sub ? 'sub' : 'big';
    const videoStat = remote.video.find(v => v.videoType === targetVideoType);
    if (!videoStat) {
      this.log.warn(`onStatistics: videoStat not found for userId=${userId}, streamType=${streamType}`);
      return;
    }
    this.networkMetrics.frameRate = videoStat.frameRate || 0;

    // 4. 获取渲染卡顿时长（从 badCaseDetector 的 monitorFreezeData 中读取，每 2s 重置）
    this.networkMetrics.renderFreezeDuration = 0;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const room = this.autoMode.instance.room as IRoom;
    if (room?.badCaseDetector) {
      const streamTypeStr = streamType === TRTCStreamType.Sub ? 'auxiliary' : 'main';
      const key = `${userId}_${streamTypeStr}`;
      const monitorFreeze = room.badCaseDetector.getMonitorFreeze();
      const freezeItem = monitorFreeze?.get(key);
      if (freezeItem) {
        this.networkMetrics.renderFreezeDuration = freezeItem.duration || 0;
      }
    }

    // 5. fps 改善率采样
    if (this.fpsImprovement.sampling) {
      const elapsed = Date.now() - this.fpsImprovement.samplingStartTime;
      if (elapsed < PlaybackQualitySwitcher.FPS_SAMPLING_DURATION) {
        this.fpsImprovement.afterSwitchFpsQueue.push(this.networkMetrics.frameRate);
      } else {
        this.reportFpsImprovement();
      }
    }

    // 6. 触发质量检测（利用 STATISTICS 事件的 2s 间隔）
    this.checkAndSwitchQuality();
  };

  /**
   * ERROR 事件回调 - 检测视频解码失败，立即下切到最低质量
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private onError = (error: PlaybackQualityErrorLike) => {
    if (!this.autoMode.enabled || !this.autoMode.instance) return;
    // extraCode 5507 = VIDEO_DECODE_FAILED
    if (error?.extraCode !== VIDEO_DECODE_FAILED_EXTRA_CODE) return;

    const decodeFailedStream = this.getDecodeFailedStream(error);
    if (!decodeFailedStream) {
      this.log.warn('auto mode: decode failed error missing stream context, skip auto downgrade');
      return;
    }

    const currentPlaying = this.getCurrentPlayingStream(this.autoMode.instance);
    if (!currentPlaying) return;
    if (decodeFailedStream.userId !== currentPlaying.userId || decodeFailedStream.streamType !== currentPlaying.streamType) {
      this.log.info(`auto mode: ignore decode failed from ${decodeFailedStream.userId}_${decodeFailedStream.streamType}, current=${currentPlaying.userId}_${currentPlaying.streamType}`);
      return;
    }

    const failedIndex = this.getQualityIndex(decodeFailedStream.userId, decodeFailedStream.streamType);
    if (failedIndex < 0) {
      this.log.warn(`auto mode: decode failed stream not found in quality list, userId=${decodeFailedStream.userId}, streamType=${decodeFailedStream.streamType}`);
      return;
    }

    const lowestIndex = this.autoMode.sortedStreamList.length - 1;
    const nextBestAllowedQualityIndex = Math.min(lowestIndex, failedIndex + 1);
    if (nextBestAllowedQualityIndex > this.autoMode.bestAllowedQualityIndex) {
      this.autoMode.bestAllowedQualityIndex = nextBestAllowedQualityIndex;
      this.log.info(`auto mode: device capability ceiling updated, bestAllowedIndex=${this.autoMode.bestAllowedQualityIndex}`);
    }

    const isUpgradeFailure = this.autoMode.lastSwitchReason === 'upgrade' && this.autoMode.lastSwitchTargetIndex === failedIndex;
    if (isUpgradeFailure) {
      this.counters.upgradeFail += 1;
      this.log.info(`auto mode: upgrade decode failed, upgradeFailCount=${this.counters.upgradeFail}/${this.autoMode.config.maxUpgradeFailCount}`);
    }

    const canDowngrade = failedIndex < lowestIndex;
    if (!canDowngrade) {
      this.log.warn('auto mode: decode failed but already at lowest quality');
      return;
    }

    const currentStream = this.autoMode.sortedStreamList[failedIndex]?.name || 'unknown';
    const targetStream = this.autoMode.sortedStreamList[lowestIndex];
    this.log.info(`auto mode decode failed downgrade: ${currentStream} → ${targetStream?.name}`);
    this.switchToQualityIndex(lowestIndex, false, 'decode-fallback');
  };

  /**
   * 检测网络质量并决定是否切换
   * 下切条件：(丢包 >= 20% && fps <= 5fps) || 渲染卡顿时长 >= 1500ms
   * 上切条件：丢包 <= 15%，距离上次下切 > 120s，且上切失败次数 < 3
   */
  private checkAndSwitchQuality() {
    if (!this.autoMode.enabled || !this.autoMode.instance) return;
    if (this.switchControl.isSwitching) return;

    const { config } = this.autoMode;
    const now = Date.now();
    const currentStream = this.autoMode.sortedStreamList[this.autoMode.currentQualityIndex]?.name || 'unknown';
    const canDowngrade = this.autoMode.currentQualityIndex < this.autoMode.sortedStreamList.length - 1;
    const canUpgrade = this.autoMode.currentQualityIndex > this.autoMode.bestAllowedQualityIndex;

    // 下切条件：(丢包高 && 帧率低) || 渲染卡顿时长超阈值
    const fpsLow = this.networkMetrics.frameRate <= config.fpsPoorLimit;
    const lossHigh = this.networkMetrics.loss >= config.lossPoorLimit;
    const renderFreezeHigh = this.networkMetrics.renderFreezeDuration >= config.renderFreezeLimit;
    const downgradeConditionMet = (lossHigh && fpsLow) || renderFreezeHigh;

    // 上切条件：丢包率达标，距离上次下切超过冷却时间
    const upgradeCooldownPassed = now - this.switchControl.lastDowngradeTime > config.cooldownTime;
    const canTryUpgrade = this.counters.upgradeFail < config.maxUpgradeFailCount;

    // 打印每次 check 的结果
    this.log.debug(`[check] stream=${currentStream}, fps=${this.networkMetrics.frameRate}, loss=${this.networkMetrics.loss}, renderFreeze=${this.networkMetrics.renderFreezeDuration}ms, ` +
      `fpsLow=${fpsLow}, lossHigh=${lossHigh}, renderFreezeHigh=${renderFreezeHigh}, downgrade=${downgradeConditionMet}, ` +
      `lossUnder=${this.counters.lossUnder}, upgradeCooldownPassed=${upgradeCooldownPassed}, ` +
      `bestAllowedIndex=${this.autoMode.bestAllowedQualityIndex}, canDowngrade=${canDowngrade}, canUpgrade=${canUpgrade && canTryUpgrade}`);

    // 下切：条件满足即触发
    if (downgradeConditionMet && canDowngrade) {
      const targetStream = this.autoMode.sortedStreamList[this.autoMode.currentQualityIndex + 1];
      this.log.info(`auto mode downgrade: ${currentStream} → ${targetStream?.name}, fps=${this.networkMetrics.frameRate}, loss=${this.networkMetrics.loss}, renderFreeze=${this.networkMetrics.renderFreezeDuration}ms`);
      this.switchToQualityIndex(this.autoMode.currentQualityIndex + 1, false, 'downgrade');
      return;
    }

    // 上切：丢包率持续达标，距离上次下切超过冷却时间，且未超过失败次数限制
    if (this.counters.lossUnder > 0 && upgradeCooldownPassed && canUpgrade && canTryUpgrade) {
      const targetStream = this.autoMode.sortedStreamList[this.autoMode.currentQualityIndex - 1];
      this.log.info(`auto mode upgrade: ${currentStream} → ${targetStream?.name}, lossUnder=${this.counters.lossUnder}`);
      this.switchToQualityIndex(this.autoMode.currentQualityIndex - 1, true, 'upgrade');
    }
  }

  /**
   * 切换到指定质量索引
   * @param index 目标质量索引
   * @param isUpgrade 是否为上切操作（用于记录上切失败次数）
   */
  private async switchToQualityIndex(index: number, isUpgrade = false, reason: AutoSwitchReason = isUpgrade ? 'upgrade' : 'downgrade') {
    if (!this.autoMode.instance || index < 0 || index >= this.autoMode.sortedStreamList.length) return;
    if (this.switchControl.isSwitching) return;
    this.switchControl.isSwitching = true;

    const targetStream = this.autoMode.sortedStreamList[index];
    const isDecodeFallback = reason === 'decode-fallback';
    this.log.info(`auto mode: switching to quality index ${index}, stream: ${targetStream.name}, reason: ${reason}, isUpgrade: ${isUpgrade}`);

    // 记录切换触发时的 fps
    const triggerFps = this.networkMetrics.frameRate;

    try {
      // 标记为内部切换，避免触发 stopAutoMode
      this.switchControl.isInternal = true;
      // 复用已有的切换逻辑
      await this.switchPlaybackQuality({
        trtcInstance: this.autoMode.instance,
        quality: targetStream.name,
        _internalSwitch: true,
        onSwitched: this.autoMode.onSwitched
      });

      this.autoMode.currentQualityIndex = index;
      this.autoMode.lastSwitchReason = reason;
      this.autoMode.lastSwitchTargetIndex = index;

      // 下切时记录下切时间（上切冷却基于下切时间）
      if (!isUpgrade) {
        this.switchControl.lastDowngradeTime = Date.now();
      }

      // 重置计数器
      this.counters.lossUnder = 0;

      // kv 上报：切换成功
      const switchResultKey = isUpgrade ? KV_REPORT_KEY_PLAYBACK_QUALITY_SWITCHER.UPGRADE_RESULT : KV_REPORT_KEY_PLAYBACK_QUALITY_SWITCHER.DOWNGRADE_RESULT;
      kvStatManager.addSuccessEvent({ key: switchResultKey });
      if (isDecodeFallback) {
        kvStatManager.addSuccessEvent({ key: KV_REPORT_KEY_PLAYBACK_QUALITY_SWITCHER.DECODE_FALLBACK_RESULT });
      }

      // 开始 fps 改善率采样（仅下切时）
      if (!isUpgrade) {
        this.startFpsSampling(triggerFps);
      }

      this.log.info(`auto mode switch success: now playing ${targetStream.name}`);
    } catch (error) {
      this.log.warn(`auto mode switch failed: ${targetStream.name}`, error);

      // kv 上报：切换失败
      const switchResultKey = isUpgrade ? KV_REPORT_KEY_PLAYBACK_QUALITY_SWITCHER.UPGRADE_RESULT : KV_REPORT_KEY_PLAYBACK_QUALITY_SWITCHER.DOWNGRADE_RESULT;
      kvStatManager.addFailedEvent({ key: switchResultKey, error: error as RtcError });
      if (isDecodeFallback) {
        kvStatManager.addFailedEvent({ key: KV_REPORT_KEY_PLAYBACK_QUALITY_SWITCHER.DECODE_FALLBACK_RESULT, error: error as RtcError });
      }

      // 上切失败，增加失败计数
      if (isUpgrade) {
        this.counters.upgradeFail += 1;
        this.log.info(`auto mode upgrade failed, upgradeFailCount=${this.counters.upgradeFail}/${this.autoMode.config.maxUpgradeFailCount}`);
      }
    } finally {
      this.switchControl.isInternal = false;
      this.switchControl.isSwitching = false;
    }
  }

  private startFpsSampling(triggerFps: number) {
    this.fpsImprovement.triggerFps = triggerFps;
    this.fpsImprovement.afterSwitchFpsQueue = [];
    this.fpsImprovement.sampling = true;
    this.fpsImprovement.samplingStartTime = Date.now();
  }

  private reportFpsImprovement() {
    this.fpsImprovement.sampling = false;
    const { triggerFps, afterSwitchFpsQueue } = this.fpsImprovement;
    if (afterSwitchFpsQueue.length === 0 || triggerFps <= 0) return;

    const avgFps = afterSwitchFpsQueue.reduce((sum, fps) => sum + fps, 0) / afterSwitchFpsQueue.length;
    const ratio = Math.floor(avgFps / triggerFps * 100);

    kvStatManager.addNumber({
      key: KV_REPORT_KEY_PLAYBACK_QUALITY_SWITCHER.FPS_IMPROVEMENT_RATIO,
      value: ratio,
      split: 1,
      max: 1000
    });
    this.log.info(`fps improvement: triggerFps=${triggerFps}, avgFps=${avgFps.toFixed(1)}, ratio=${ratio}%`);
  }
}
